import React, { createContext, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../api/ApiClientContext';

interface ConflictHandlerContextType {
  /**
   * Handles a 409 conflict by fetching user-flow-status and navigating accordingly.
   * Returns true if it was a 409 and was handled, false otherwise.
   */
  handleConflict: (error: any) => Promise<boolean>;
  
  /**
   * Wraps an async function to automatically handle 409 conflicts.
   * Use this for mutations or any API calls that might return 409.
   */
  withConflictHandler: <T>(fn: () => Promise<T>) => Promise<T>;
}

class ConflictHandledError extends Error {
  __conflictHandled = true;
  constructor(originalError: unknown) {
    super(`Conflict handled: ${String(originalError)}`);
    this.name = 'ConflictHandledError';
  }
}

const ConflictHandlerContext = createContext<ConflictHandlerContextType | null>(null);

interface ConflictHandlerProviderProps {
  children: React.ReactNode;
}

export const ConflictHandlerProvider: React.FC<ConflictHandlerProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const apiClient = useApiClient();
  
  // Prevent multiple simultaneous conflict handling
  const isHandlingConflict = useRef(false);

  /**
   * Determines if an error is a 409 Conflict
   */
  const isConflictError = useCallback((error: any): boolean => {
    // Handle different error formats
    if (error?.status === 409) return true;
    if (error?.response?.status === 409) return true;
    if (error?.error?.status === 409) return true;
    return false;
  }, []);

  /**
   * Fetches user-flow-status and navigates to the appropriate route
   */
  const syncAndNavigate = useCallback(async (): Promise<void> => {
    try {
      const response = await apiClient.api.getUserFlowStatusApiV1AuthUserFlowStatusGet();
      const flowStatus = response?.data;

      if (!flowStatus) {
        console.warn('[ConflictHandler] No flow status returned');
        return;
      }

      // Invalidate all relevant queries to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['userFlowStatus'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      await queryClient.invalidateQueries({ queryKey: ['habits'] });
      await queryClient.invalidateQueries({ queryKey: ['myActiveHabits'] });

      const { navigation_path, has_accepted_terms } = flowStatus;

      console.log('[ConflictHandler] Flow status:', { navigation_path, has_accepted_terms });

      if (has_accepted_terms === false) {
        window.location.reload();
        return;
      }

      // Navigate based on navigation_path
      switch (navigation_path) {
        case 'platform_onboarding':
        case 'onboarding':
        case 'dashboard':
          navigate('/', { replace: true });
          break;
        case 'leaderboard':
          navigate('/leaderboard', { replace: true });
          break;
        case 'about':
          navigate('/about', { replace: true });
          break;
        default:
          navigate('/', { replace: true });
      }
    } catch (error) {
      console.error('[ConflictHandler] Failed to fetch user flow status:', error);
      // On error, refresh the page as a fallback
      window.location.reload();
    }
  }, [apiClient, queryClient, navigate]);

  /**
   * Main conflict handler - call this when you catch a 409
   */
  const handleConflict = useCallback(async (error: any): Promise<boolean> => {
    if (!isConflictError(error)) {
      return false;
    }

    // Prevent multiple simultaneous conflict handlers
    if (isHandlingConflict.current) {
      console.log('[ConflictHandler] Already handling a conflict, skipping');
      return true;
    }

    isHandlingConflict.current = true;
    console.log('[ConflictHandler] 409 Conflict detected, syncing user flow status...');

    try {
      await syncAndNavigate();
      return true;
    } finally {
      // Reset after a short delay to prevent rapid re-triggers
      setTimeout(() => {
        isHandlingConflict.current = false;
      }, 1000);
    }
  }, [isConflictError, syncAndNavigate]);

  /**
   * Wrapper for async functions that automatically handles 409 conflicts
   */
  const withConflictHandler = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
    try {
      return await fn();
    } catch (error: any) {
      const wasHandled = await handleConflict(error);
      if (wasHandled) {
        // Return a rejected promise that won't trigger error UI
        // The navigation will handle the user experience
        throw new ConflictHandledError(error);
      }
      throw error;
    }
  }, [handleConflict]);

  return (
    <ConflictHandlerContext.Provider value={{ handleConflict, withConflictHandler }}>
      {children}
    </ConflictHandlerContext.Provider>
  );
};

/**
 * Hook to access the conflict handler
 */
export const useConflictHandler = (): ConflictHandlerContextType => {
  const context = useContext(ConflictHandlerContext);
  if (!context) {
    throw new Error('useConflictHandler must be used within a ConflictHandlerProvider');
  }
  return context;
};

/**
 * Type guard to check if an error was already handled by the conflict handler
 */
export const isConflictHandledError = (error: any): boolean => {
  return error?.__conflictHandled === true;
};

